import { useEffect, useState } from "react";
import { Plus, Search, PawPrint, Wallet, Award, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { peso, fmtErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import ReceiptModal from "../../components/ReceiptModal";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const EMPTY_C = { name: "", email: "", phone: "", address: "", senior_pwd_id: "" };
const EMPTY_P = { name: "", species: "Dog", breed: "", weight_kg: "", coat_type: "short", birthdate: "", vaccine_notes: "", allergies: "", notes: "" };

export default function Customers() {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY_C);
  const [detail, setDetail] = useState(null);
  const [petDialog, setPetDialog] = useState(false);
  const [petForm, setPetForm] = useState(EMPTY_P);
  const [creditDialog, setCreditDialog] = useState(false);
  const [creditForm, setCreditForm] = useState({ amount: "", reason: "" });
  const [receiptId, setReceiptId] = useState(null);

  const load = () => api.get("/customers", { params: { q } }).then((r) => setCustomers(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, [q]);

  const openDetail = (id) => api.get(`/customers/${id}`).then((r) => setDetail(r.data)).catch((e) => toast.error(fmtErr(e)));

  const saveCustomer = async () => {
    try {
      await api.post("/customers", form);
      toast.success("Customer added");
      setDialog(false); setForm(EMPTY_C); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const savePet = async () => {
    try {
      await api.post(`/customers/${detail.id}/pets`, { ...petForm, weight_kg: parseFloat(petForm.weight_kg) || 0 });
      toast.success("Pet added");
      setPetDialog(false); setPetForm(EMPTY_P); openDetail(detail.id);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const adjustCredit = async () => {
    try {
      const { data } = await api.post(`/customers/${detail.id}/credit`, { amount: parseFloat(creditForm.amount) || 0, reason: creditForm.reason });
      toast.success(`Store credit now ${peso(data.store_credit)}`);
      setCreditDialog(false); setCreditForm({ amount: "", reason: "" }); openDetail(detail.id); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="animate-rise space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight" data-testid="customers-title">Customers &amp; Pets</h1>
          <p className="text-sm text-slate-500">Unified profiles linking owners to their pets, credit and loyalty.</p>
        </div>
        <Button className="bg-[#0E7C84] hover:bg-[#093F46]" onClick={() => setDialog(true)} data-testid="add-customer-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Customer
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input className="pl-9 bg-white" placeholder="Search name, email or phone…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="customer-search" />
      </div>

      <div className="bg-white rounded-2xl border border-[#BFE8E9] overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead className="text-right">Paw Points</TableHead><TableHead className="text-right">Store Credit</TableHead><TableHead>SC/PWD ID</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {customers.map((c) => (
              <TableRow key={c.id} className="cursor-pointer hover:bg-[#F2FBFB]" onClick={() => openDetail(c.id)} data-testid={`customer-row-${c.id.slice(0, 8)}`}>
                <TableCell className="font-medium">{c.name} {c.user_id && <Badge variant="outline" className="ml-1 text-[9px]">portal</Badge>}</TableCell>
                <TableCell className="text-sm text-slate-500">{c.email || "—"}<br />{c.phone}</TableCell>
                <TableCell className="text-right"><span className="inline-flex items-center gap-1 font-semibold"><Award className="w-3.5 h-3.5 text-[#F4A261]" />{c.loyalty_points}</span></TableCell>
                <TableCell className="text-right font-semibold text-[#2A9D8F]">{peso(c.store_credit)}</TableCell>
                <TableCell className="text-xs font-mono">{c.senior_pwd_id || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent data-testid="customer-dialog">
          <DialogHeader><DialogTitle className="font-display">New customer</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="customer-email" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="customer-phone" /></div>
            <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="customer-address" /></div>
            <div className="col-span-2"><Label>Senior/PWD ID (optional)</Label><Input value={form.senior_pwd_id} onChange={(e) => setForm({ ...form, senior_pwd_id: e.target.value })} data-testid="customer-senior-id" /></div>
          </div>
          <Button className="bg-[#0E7C84] hover:bg-[#093F46] mt-2" onClick={saveCustomer} disabled={!form.name} data-testid="customer-save-btn">Add customer</Button>
        </DialogContent>
      </Dialog>

      <Sheet open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="customer-detail-drawer">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display text-xl">{detail.name}</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-5">
                <div className="text-sm text-slate-600 space-y-1">
                  <p>{detail.email || "No email"} · {detail.phone || "No phone"}</p>
                  <p>{detail.address}</p>
                  {detail.senior_pwd_id && <p className="font-mono text-xs">SC/PWD ID: {detail.senior_pwd_id}</p>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-[#E2F5F5] rounded-xl p-3">
                    <p className="text-xs text-slate-500 flex items-center gap-1"><Award className="w-3.5 h-3.5 text-[#F4A261]" /> Paw Points</p>
                    <p className="font-display font-bold text-xl">{detail.loyalty_points}</p>
                  </div>
                  <div className="bg-[#E2F5F5] rounded-xl p-3">
                    <p className="text-xs text-slate-500 flex items-center gap-1"><Wallet className="w-3.5 h-3.5 text-[#2A9D8F]" /> Store Credit</p>
                    <p className="font-display font-bold text-xl">{peso(detail.store_credit)}</p>
                    {["admin", "manager"].includes(user?.role) && (
                      <button className="text-xs text-[#0E7C84] font-semibold mt-1" onClick={() => setCreditDialog(true)} data-testid="adjust-credit-btn">Adjust</button>
                    )}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-display font-semibold flex items-center gap-2"><PawPrint className="w-4 h-4 text-[#E84855]" /> Pets ({detail.pets.length})</h4>
                    <Button variant="outline" size="sm" onClick={() => setPetDialog(true)} data-testid="add-pet-staff-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add pet</Button>
                  </div>
                  <div className="space-y-2">
                    {detail.pets.map((p) => (
                      <div key={p.id} className="border border-[#BFE8E9] rounded-xl p-3 text-sm" data-testid={`detail-pet-${p.name.toLowerCase()}`}>
                        <p className="font-semibold">{p.name} <span className="text-slate-400 font-normal">· {p.species} · {p.breed}</span></p>
                        <p className="text-xs text-slate-500">{p.weight_kg}kg ({p.weight_tier}) · {p.coat_type} coat</p>
                        {p.vaccine_notes && <p className="text-xs text-[#0E7C84] mt-1">Vaccines: {p.vaccine_notes}</p>}
                        {p.allergies && <p className="text-xs text-red-600">Allergies: {p.allergies}</p>}
                      </div>
                    ))}
                    {detail.pets.length === 0 && <p className="text-sm text-slate-400">No pets registered yet.</p>}
                  </div>
                </div>

                <div>
                  <h4 className="font-display font-semibold mb-2">Purchase history</h4>
                  <div className="space-y-2">
                    {detail.recent_sales.map((s) => (
                      <button key={s.id} className="w-full text-left border border-[#BFE8E9] rounded-xl p-3 text-sm hover:border-[#0E7C84] transition-colors" onClick={() => setReceiptId(s.id)} data-testid={`history-${s.receipt_no}`}>
                        <div className="flex justify-between"><span className="font-mono font-semibold">{s.receipt_no}</span><span className="font-bold text-[#0E7C84]">{peso(s.total)}</span></div>
                        <p className="text-xs text-slate-500">{s.created_at?.slice(0, 10)} · {s.items.length} item/s · {s.channel}</p>
                      </button>
                    ))}
                    {detail.recent_sales.length === 0 && <p className="text-sm text-slate-400">No purchases yet.</p>}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={petDialog} onOpenChange={setPetDialog}>
        <DialogContent data-testid="staff-pet-dialog">
          <DialogHeader><DialogTitle className="font-display">Add pet for {detail?.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={petForm.name} onChange={(e) => setPetForm({ ...petForm, name: e.target.value })} data-testid="staff-pet-name" /></div>
            <div>
              <Label>Species</Label>
              <Select value={petForm.species} onValueChange={(v) => setPetForm({ ...petForm, species: v })}>
                <SelectTrigger data-testid="staff-pet-species"><SelectValue /></SelectTrigger>
                <SelectContent>{["Dog", "Cat", "Bird", "Other"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Breed</Label><Input value={petForm.breed} onChange={(e) => setPetForm({ ...petForm, breed: e.target.value })} data-testid="staff-pet-breed" /></div>
            <div><Label>Weight (kg)</Label><Input type="number" step="0.1" value={petForm.weight_kg} onChange={(e) => setPetForm({ ...petForm, weight_kg: e.target.value })} data-testid="staff-pet-weight" /></div>
            <div>
              <Label>Coat type</Label>
              <Select value={petForm.coat_type} onValueChange={(v) => setPetForm({ ...petForm, coat_type: v })}>
                <SelectTrigger data-testid="staff-pet-coat"><SelectValue /></SelectTrigger>
                <SelectContent>{["short", "long", "double", "matted", "hairless"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Birthdate</Label><Input type="date" value={petForm.birthdate} onChange={(e) => setPetForm({ ...petForm, birthdate: e.target.value })} data-testid="staff-pet-birthdate" /></div>
            <div className="col-span-2"><Label>Vaccination records</Label><Input value={petForm.vaccine_notes} onChange={(e) => setPetForm({ ...petForm, vaccine_notes: e.target.value })} data-testid="staff-pet-vaccines" /></div>
            <div className="col-span-2"><Label>Allergies</Label><Input value={petForm.allergies} onChange={(e) => setPetForm({ ...petForm, allergies: e.target.value })} data-testid="staff-pet-allergies" /></div>
          </div>
          <Button className="bg-[#0E7C84] hover:bg-[#093F46] mt-2" onClick={savePet} disabled={!petForm.name} data-testid="staff-pet-save">Add pet</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={creditDialog} onOpenChange={setCreditDialog}>
        <DialogContent data-testid="credit-dialog">
          <DialogHeader><DialogTitle className="font-display">Adjust store credit</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Amount (+ add / − deduct)</Label><Input type="number" step="0.01" value={creditForm.amount} onChange={(e) => setCreditForm({ ...creditForm, amount: e.target.value })} data-testid="credit-amount" /></div>
            <div><Label>Reason</Label><Input value={creditForm.reason} onChange={(e) => setCreditForm({ ...creditForm, reason: e.target.value })} data-testid="credit-reason" /></div>
            <Button className="w-full bg-[#0E7C84] hover:bg-[#093F46]" onClick={adjustCredit} disabled={!creditForm.amount} data-testid="credit-save-btn">Apply</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ReceiptModal saleId={receiptId} open={!!receiptId} onClose={() => setReceiptId(null)} allowEmail />
    </div>
  );
}
